import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import { noteEditorExtensions } from "./noteEditor/extensions";
import { noteEditorProps } from "./noteEditor/pasteImage";
import { NoteToolbar } from "./NoteToolbar";
import { IconNoteFormat } from "./noteEditor/icons";
import { useNotePrefs } from "../hooks/useNotePrefs";
import { collapse } from "../motion";
import "../styles/note-editor.css";

/**
 * Day-note writing surface (ADR-0009/0010/0011): a TipTap WYSIWYG editor with
 * a compact, Google-Docs-like formatting toolbar (toggleable, per-device
 * preference) covering text size/colour/highlight/underline/strikethrough,
 * links, lists, alignment, indent, line+paragraph spacing and images.
 * Markdown-while-typing input rules from StarterKit remain active. When the
 * toolbar is hidden its head strip collapses, so the writing canvas expands
 * into the freed space; the always-visible toggle floats at the card's
 * top-right corner.
 */
export function DayNoteEditor({ value, onChange }: { value: string; onChange: (html: string) => void }) {
  // TipTap's useEditor re-applies its options whenever the options object
  // identity changes across renders (setOptions -> view.updateState ->
  // transaction -> re-render), which would loop forever. So the options
  // object is created once per mount and mutable callbacks are read through
  // refs.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const editorRef = useRef<Editor | null>(null);
  const openLinkRef = useRef<() => void>(() => {});
  const [linkOpen, setLinkOpen] = useState(false);
  const initialContent = useRef(value).current;
  const { toolbarOpen, setToolbarOpen, showToolbarTransient } = useNotePrefs();

  const options = useMemo(
    () => ({
      extensions: noteEditorExtensions,
      content: initialContent,
      editorProps: noteEditorProps(() => editorRef.current, () => openLinkRef.current()),
      onUpdate: ({ editor: ed }: { editor: Editor }) => onChangeRef.current(ed.getHTML()),
    }),
    [],
  );

  const editor = useEditor(options);
  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);
  // Ctrl/Cmd+K toggles the link popover (wired through a ref so the
  // once-created editorProps closure stays stable). The popover needs the
  // toolbar visible, so surface it too — but without persisting the choice.
  useEffect(() => {
    openLinkRef.current = () => {
      setLinkOpen((open) => !open);
      showToolbarTransient();
    };
  }, [showToolbarTransient]);

  const lastValue = useRef(value);

  // Push externally supplied content (server note / restored draft) into the
  // editor. Skipped when the editor already matches, so typing never resets
  // the cursor or document.
  useEffect(() => {
    if (!editor || lastValue.current === value) return;
    lastValue.current = value;
    if (editor.getHTML() !== value) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [editor, value]);

  if (!editor) {
    return <div className="note-editor-loading muted">Loading editor…</div>;
  }

  return (
    <div className={`note-editor${toolbarOpen ? " is-toolbar-open" : ""}`}>
      <div className="note-editor-head">
        <AnimatePresence initial={false}>
          {toolbarOpen ? (
            <motion.div className="note-toolbar-collapse" variants={collapse} initial="hidden" animate="show" exit="exit">
              <NoteToolbar editor={editor} linkOpen={linkOpen} setLinkOpen={setLinkOpen} />
            </motion.div>
          ) : null}
        </AnimatePresence>
        <button
          type="button"
          className={`note-toolbar-toggle${toolbarOpen ? " is-on" : ""}`}
          title={toolbarOpen ? "Hide formatting toolbar" : "Show formatting toolbar"}
          aria-label={toolbarOpen ? "Hide formatting toolbar" : "Show formatting toolbar"}
          aria-pressed={toolbarOpen}
          onClick={() => setToolbarOpen(!toolbarOpen)}
        >
          <IconNoteFormat />
        </button>
      </div>
      <EditorContent editor={editor} className="tiptap" />
    </div>
  );
}